import { useSelector } from 'react-redux';
import { useGetOrganizationQuery } from 'uf-api-rtk/store/Api';
import { getActiveOrganizationKey } from 'uf/app/selectors';

interface OrgHelpURLResult {
  data?: string;
  isLoading: boolean;
  isError: boolean;
}

const useOrgHelpURL = (): OrgHelpURLResult => {
  const orgKey = useSelector(getActiveOrganizationKey);
  const {
    data: orgData,
    isLoading,
    isError,
  } = useGetOrganizationQuery({ key: orgKey }, { skip: !orgKey });
  return {
    data: orgData?.help_url,
    isLoading,
    isError,
  };
};

/**
 * This is restricted to the hook, for now, as the ModuleKey type defined by
 * the API schema is string. If we want to cover the entirety of the app
 * it will require a BE schema change and some code changes to to fix TS errors.
 */
type ModuleKey =
  | 'household_cost'
  | 'tnc_conservation'
  | 'energy_demand'
  | 'water_demand'
  | 'ghg_emission'
  | 'resiliency'
  | 'transit_accessibility'
  | 'transportation_anywhere'
  | 'walk_accessibility'
  | 'land_consumption';

export const moduleContextIds: Record<ModuleKey, string | null> = {
  household_cost: 'householdcostsanalysis',
  tnc_conservation: null, // Special case, hardcoded URL
  energy_demand: 'energyanalysis',
  water_demand: 'wateruseanalysis',
  ghg_emission: 'emissionsanalysis',
  resiliency: 'riskresilienceanalysis',
  transit_accessibility: 'walktransitanalysis',
  transportation_anywhere: 'transportationanalysis',
  walk_accessibility: 'walktransitanalysis',
  land_consumption: 'landconsumptionanalysis',
};

export const getMethodologyLink = (
  baseUrl: string | undefined,
  contextId: string,
): string | undefined => {
  if (!baseUrl || !contextId) {
    return undefined;
  }
  return `${baseUrl}?contextId=${contextId}`;
};

interface OrgMethodologyLinks {
  [key: string]: string | undefined;
}

const useOrgMethodologyLinks = (): OrgMethodologyLinks => {
  const { data: helpURL } = useOrgHelpURL();

  const links: OrgMethodologyLinks = Object.entries(moduleContextIds).reduce(
    (acc, [moduleKey, contextId]) => {
      if (moduleKey === 'tnc_conservation') {
        acc[moduleKey] =
          'https://docs.urbanfootprint.com/assets/UF-Conservation-Module-Methodology.pdf';
      } else {
        acc[moduleKey] = getMethodologyLink(helpURL, contextId);
      }
      return acc;
    },
    {},
  );

  return links;
};

export { useOrgHelpURL, useOrgMethodologyLinks };
